import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { Component, HostListener, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { UserService } from '../../shared/services/user.service';
import { StringConstants } from '../constants/string-constants';
import { PassdataService } from '../services/passdata.service';
import { RecombinationsService } from '../services/recombinations.service';

@Component({
  standalone: false,
  selector: 'invd-confirmation-page',
  templateUrl: './confirmation-page.html',
  styleUrls: ['./confirmation-page.scss'],
})
export class ConfirmationComponent implements OnInit, AfterViewInit, OnDestroy {
  public distributedBondsDataProvider: Array<any>;
  public receivedBondsDataProvider: Array<any>;
  public headerText: string;
  public exchangeConfirmationNumber: string;
  public actionMessage: string;
  public dealNumber: string;
  public dealerName: string;
  public traderName: string;
  public exchangeSettlementDate: string;
  public memoText: string;
  public feeCurrentFaceNotional: number;
  public feeCurrentFace: number;
  public feeDollars: string;
  public feeTicks: string;
  public exchangeType: string;
  public displayRecomColumn: boolean;
  public isTakedownFlow: boolean;
  public showBanner = false;
  public messageTitle = '';
  public messageBody = '';
  public messageType = '';
  public displayBackBtnClickPanel = false;
  public browserBackBtnClicked = false;
  public downloadPDFResponse: any;
  public req: any;
  public showEarlyCloseBanner = false;
  public earlyCloseMessageTitle = '';
  public earlyCloseMessageBody = '';
  public earlyCloseMessageType = '';
  public warningMessages: any[] = [];
  public infoMessage: string[] = [];
  public isAllowToNavigateAway = true;
  public exportPDFButtonClicked = false;

  private passDataService = inject(PassdataService);
  private rcrService = inject(RecombinationsService);
  public fmLoadingPageService = inject(FmLoadingPageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private userService = inject(UserService);

  @HostListener('window:beforeunload', ['$event'])
  refreshBtnClick($event) {
    $event.returnValue = '';
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(_$event) {
    this.browserBackBtnClicked = true;
  }

  ngOnInit() {
    this.fmLoadingPageService.stopLoading();
    if (this.passDataService.getCommitExchangeResponse() != null) {
      const responseObject: any = this.passDataService.getCommitExchangeResponse();
      this.exchangeType = responseObject.exchangeType;
      this.exchangeConfirmationNumber = responseObject.exchangeConfirmationNumber.toString();
      this.actionMessage = responseObject.actionMessage;
      this.dealNumber = responseObject.dealNumber;
      this.dealerName = responseObject.dealerName;
      this.traderName = responseObject.traderName;
      const settlementDateObject = new Date(responseObject.exchangeSettlementDate);
      const options: Intl.DateTimeFormatOptions = {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      };
      this.exchangeSettlementDate = settlementDateObject.toLocaleDateString('en-US', options);
      this.memoText = responseObject.memoText;
      this.feeCurrentFaceNotional = responseObject.feeCurrentFaceNotional;
      this.feeCurrentFace = responseObject.feeCurrentFace;
      this.feeDollars = responseObject.feeDollars;
      this.feeTicks = responseObject.feeTicks;
      this.infoMessage = responseObject && responseObject.infoMessages;
      if (responseObject.netExchangeViews.length > 0) {
        this.distributedBondsDataProvider = responseObject.netExchangeViews[0].netToFNMA;
        this.receivedBondsDataProvider = responseObject.netExchangeViews[0].netToDealer;
      }
    } else {
      this.router.navigate(['./']);
    }

    if (!this.route.snapshot.data[0].isSettledExchangeConfirmation) {
      this.displayBanner();
    }

    this.getExchangeType();
    this.displayEarlyCloseBanner(this.infoMessage);
  }

  ngAfterViewInit() {
    // this.getExchangeType();
    this.navigationController();
  }

  canDeactivate(): Promise<boolean> | boolean {
    if (this.browserBackBtnClicked) {
      this.displayBackBtnClickPanel = true;
      return false;
    } else {
      return this.isAllowToNavigateAway;
    }
  }

  ngOnDestroy() {
    this.passDataService.setCommitExchangeResponse(null);
    this.clearUserService();
  }

  private navigationController() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('review-and-commit') > 0) {
          this.isAllowToNavigateAway = false;
        } else if (event.url.indexOf('enter-balances') > 0) {
          this.isAllowToNavigateAway = false;
        } else if (event.url.indexOf('enter-takedowns') > 0) {
          this.isAllowToNavigateAway = false;
        } else {
          this.isAllowToNavigateAway = true;
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
    });
  }

  private getExchangeType() {
    if (this.exchangeType === StringConstants.EXCHANGE_TYPE_REMIC) {
      this.displayRecomColumn = true;
      this.isTakedownFlow = false;
      this.headerText = StringConstants.EXCHANGE_CONFIRMED;
    } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS) {
      this.displayRecomColumn = false;
      this.isTakedownFlow = false;
      this.headerText = StringConstants.EXCHANGE_CONFIRMED;
    } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_PREDEFINED_SMBS) {
      this.displayRecomColumn = true;
      this.isTakedownFlow = false;
      this.headerText = StringConstants.EXCHANGE_CONFIRMED;
    } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_REMIC) {
      this.displayRecomColumn = true;
      this.isTakedownFlow = true;
      this.headerText = StringConstants.TAKEDOWN_CONFIRMED;
    } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_EXPD_SMBS) {
      this.displayRecomColumn = false;
      this.isTakedownFlow = true;
      this.headerText = StringConstants.TAKEDOWN_CONFIRMED;
    } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_PREDEFINED_SMBS) {
      this.displayRecomColumn = true;
      this.isTakedownFlow = true;
      this.headerText = StringConstants.TAKEDOWN_CONFIRMED;
    }
  }

  private displayBanner(): void {
    this.showBanner = true;
    this.messageTitle = 'Status';
    if (!this.userService.isInternalUser) {
      this.messageBody = `You have successfully completed this exchange.`;
    } else {
      this.messageBody = `You have successfully completed this exchange on behalf of  ${this.userService.getTraderName()} at ${this.userService.getDealerName()}`;
    }
    this.messageType = StringConstants.MESSAGE_SUCCESS;
  }

  private displayEarlyCloseBanner(displayBanner): void {
    if (displayBanner !== '' && displayBanner !== null && displayBanner !== undefined) {
      this.showEarlyCloseBanner = true;
      /*this.earlyCloseMessageTitle = displayBanner.split('.')[0] + ".";
       this.earlyCloseMessageBody  = displayBanner.split('.')[1] + ".";
       this.earlyCloseMessageType  = StringConstants.MESSAGE_NOTICE;*/
      this.warningMessages = [];
      displayBanner.map((messageInfo) => {
        const title = 'Status';
        /*messageInfo.split('.')[0] + '.';*/
        const message = messageInfo;
        /*messageInfo.split('.')[1] + '.';*/
        const type = StringConstants.MESSAGE_NOTICE;
        this.warningMessages.push({ title, message, type });
      });
    }
  }

  private clearUserService(): void {
    this.userService.setTrader(null, null);
    this.userService.setDealerName(null);
  }

  public dismissPopup(): void {
    this.displayBackBtnClickPanel = false;
    this.browserBackBtnClicked = false;
  }

  public downloadPDF(): void {
    this.exportPDFButtonClicked = true;

    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.req = {
      exchangeConfirmationNumber: this.exchangeConfirmationNumber,
    };
    this.rcrService.downloadExchangePDF(this.req).subscribe({
      next: (data) => this.handleDownloadPDFServiceSuccess(data),
      error: (error) => this.handleAWSDownloadPDFServiceFail(error),
    });
  }

  private handleDownloadPDFServiceSuccess(downloadPDFRes) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.downloadPDFResponse = downloadPDFRes;

    let data = this.downloadPDFResponse;
    let dealTypeText = '_Exchange_Confirmation_';
    if (this.isTakedownFlow) {
      dealTypeText = '_Takedown_Confirmation_';
    }
    const fileName = this.dealNumber + dealTypeText + this.exchangeConfirmationNumber + '.pdf';
    data = data.replace(/^[^,]+,/, '');
    data = data.replace(/\s/g, '');
    const byteCharacters = window.atob(data);
    const byteNumbers = Array.from({ length: byteCharacters.length });
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers as any);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      //window.open("data:application/pdf;base64," + this.downloadPDFResponse);
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;

      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }
    this.exportPDFButtonClicked = false;
  }

  private handleAWSDownloadPDFServiceFail(_error: any): void {
    this.exportPDFButtonClicked = false;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
